import { useState, useEffect } from 'react';
import { adminApi, ensureArray } from '../services/api';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface Article {
  id: string;
  title: string;
  slug: string;
  category: string;
  language: string;
  publishedAt: string | null;
  authorName: string;
  featuredOnHome: boolean;
  homeSortOrder: number | null;
  kind?: 'article' | 'video' | 'exercise';
}

interface ArticleDetail extends Article {
  summary: string;
  body: string;
  expandParagraphs: string[];
  imageUrl: string | null;
}

interface ArticleForm {
  title: string;
  slug: string;
  summary: string;
  expandParagraphsText: string;
  body: string;
  category: string;
  language: string;
  imageUrl: string;
  authorName: string;
  publishedAt: string;
  featuredOnHome: boolean;
  homeSortOrder: string;
  kind: 'article' | 'video' | 'exercise';
}

const empty = (): ArticleForm => ({
  title: '',
  slug: '',
  summary: '',
  expandParagraphsText: '',
  body: '',
  category: 'Mental health',
  language: 'en',
  imageUrl: '',
  authorName: 'MyLighthouse Team',
  publishedAt: '',
  featuredOnHome: false,
  homeSortOrder: '',
  kind: 'article',
});

const CATEGORIES = [
  'Mental health',
  'Wellness',
  'Getting started',
  'Anxiety',
  'Depression',
  'Relationships',
  'Grief',
  'Stress',
  'Self-care',
  'Therapy Tips',
  'Parenting',
];
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'yo', label: 'Yoruba' },
  { code: 'ig', label: 'Igbo' },
  { code: 'ha', label: 'Hausa' },
];

function paragraphsToText(paragraphs: string[]): string {
  return paragraphs.join('\n\n');
}

function textToParagraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function articleToForm(article: ArticleDetail): ArticleForm {
  return {
    title: article.title,
    slug: article.slug,
    summary: article.summary,
    expandParagraphsText: paragraphsToText(article.expandParagraphs),
    body: article.body,
    category: article.category,
    language: article.language,
    imageUrl: article.imageUrl ?? '',
    authorName: article.authorName,
    publishedAt: toDatetimeLocalValue(article.publishedAt),
    featuredOnHome: article.featuredOnHome,
    homeSortOrder: article.homeSortOrder != null ? String(article.homeSortOrder) : '',
    kind: article.kind ?? 'article',
  };
}

function formToPayload(form: ArticleForm) {
  const homeSortOrder = form.homeSortOrder.trim() ? Number.parseInt(form.homeSortOrder, 10) : null;
  return {
    title: form.title,
    slug: form.slug,
    summary: form.summary,
    expandParagraphs: textToParagraphs(form.expandParagraphsText),
    body: form.body,
    category: form.category,
    language: form.language,
    imageUrl: form.imageUrl,
    authorName: form.authorName,
    publishedAt: fromDatetimeLocalValue(form.publishedAt),
    featuredOnHome: form.featuredOnHome,
    homeSortOrder: Number.isFinite(homeSortOrder) ? homeSortOrder : null,
    kind: form.kind,
  };
}

export function ContentPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<ArticleForm>(empty());
  const [saving, setSaving] = useState(false);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    adminApi<Article[]>('/content/manage')
      .then((data) => setArticles(ensureArray(data)))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!editing || editing === 'new') return;
    setLoadingArticle(true);
    setError('');
    adminApi<ArticleDetail>(`/content/manage/${editing}`)
      .then((article) => setForm(articleToForm(article)))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoadingArticle(false));
  }, [editing]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = formToPayload(form);
      if (editing === 'new') {
        await adminApi('/content', { method: 'POST', body: JSON.stringify(payload) });
      } else if (editing) {
        await adminApi(`/content/${editing}`, { method: 'PUT', body: JSON.stringify(payload) });
      }
      setEditing(null);
      setForm(empty());
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };

  const f =
    (field: keyof ArticleForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value =
        e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const slugify = (title: string) =>
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  if (editing) {
    return (
      <div className="max-w-2xl">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => {
              setEditing(null);
              setForm(empty());
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← Back
          </button>
          <h1 className="text-xl font-bold text-gray-900">
            {editing === 'new' ? 'New resource' : 'Edit resource'}
          </h1>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {loadingArticle ? (
          <div className="py-12 text-center text-gray-400">Loading article…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                className="input-field mt-1 w-full"
                value={form.title}
                onChange={(e) => {
                  setForm((p) => ({
                    ...p,
                    title: e.target.value,
                    slug: editing === 'new' ? slugify(e.target.value) : p.slug,
                  }));
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Slug</label>
              <input
                className="input-field mt-1 w-full font-mono text-sm"
                value={form.slug}
                onChange={f('slug')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <select className="input-field mt-1 w-full" value={form.kind} onChange={f('kind')}>
                <option value="article">Article</option>
                <option value="video">Video</option>
                <option value="exercise">Exercise</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Category</label>
                <select
                  className="input-field mt-1 w-full"
                  value={form.category}
                  onChange={f('category')}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Language</label>
                <select
                  className="input-field mt-1 w-full"
                  value={form.language}
                  onChange={f('language')}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Card summary (caption on collapsed card)
              </label>
              <textarea
                className="input-field mt-1 w-full"
                rows={2}
                value={form.summary}
                onChange={f('summary')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Expandable paragraphs (shown when “Read more” is tapped)
              </label>
              <p className="mt-1 text-xs text-gray-500">Separate paragraphs with a blank line.</p>
              <textarea
                className="input-field mt-1 w-full"
                rows={6}
                value={form.expandParagraphsText}
                onChange={f('expandParagraphsText')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Full article body (plain text / HTML)
              </label>
              <textarea
                className="input-field mt-1 w-full font-mono text-sm"
                rows={12}
                value={form.body}
                onChange={f('body')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Author</label>
                <input
                  className="input-field mt-1 w-full"
                  value={form.authorName}
                  onChange={f('authorName')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Publish date (blank = draft)
                </label>
                <input
                  type="datetime-local"
                  className="input-field mt-1 w-full"
                  value={form.publishedAt}
                  onChange={f('publishedAt')}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Image URL (optional)
              </label>
              <input
                className="input-field mt-1 w-full"
                value={form.imageUrl}
                onChange={f('imageUrl')}
                placeholder="https://..."
              />
            </div>
            <div className="rounded-xl border border-white/60 bg-white/40 p-4">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.featuredOnHome}
                  onChange={f('featuredOnHome')}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-800">Feature on home page</span>
              </label>
              {form.featuredOnHome && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Home display order (lower = first)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    className="input-field mt-1 w-32"
                    value={form.homeSortOrder}
                    onChange={f('homeSortOrder')}
                    placeholder="0"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving} className="btn-primary px-6">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setEditing(null);
                  setForm(empty());
                }}
                className="btn-secondary px-6"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Resources</h1>
          <p className="mt-1 text-sm text-gray-500">
            Articles, videos, and exercises shown in the app. Mark “Feature on home” to show a
            resource under the home screen link.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing('new');
            setForm(empty());
          }}
          className="btn-primary text-sm"
        >
          + New resource
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-400">Loading…</div>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 text-xs font-semibold uppercase text-gray-500">
            <tr>
              <th className="py-2 pr-4">Title</th>
              <th className="py-2 pr-4">Category</th>
              <th className="py-2 pr-4">Home</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {articles.map((a) => (
              <tr key={a.id} className="hover:bg-gray-50">
                <td className="py-3 pr-4 font-medium text-gray-900">{a.title}</td>
                <td className="py-3 pr-4 text-gray-500">{a.category}</td>
                <td className="py-3 pr-4 text-gray-500">
                  {a.featuredOnHome ? (
                    <span className="text-primary-700 font-medium">
                      Featured{a.homeSortOrder != null ? ` (#${a.homeSortOrder + 1})` : ''}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="py-3 pr-4">
                  {a.publishedAt ? (
                    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                      Published
                    </span>
                  ) : (
                    <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700">
                      Draft
                    </span>
                  )}
                </td>
                <td className="py-3">
                  <div className="flex gap-3">
                    <button
                      onClick={() => setEditing(a.id)}
                      className="text-primary-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        void handleDelete(a.id);
                      }}
                      className="text-red-500 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ConfirmDialog
        open={deleteId !== null}
        title="Delete resource?"
        message="Delete this resource? This cannot be undone."
        confirmLabel="Delete"
        danger
        onCancel={() => setDeleteId(null)}
        onConfirm={async () => {
          if (!deleteId) return;
          await adminApi(`/content/${deleteId}`, { method: 'DELETE' });
          setDeleteId(null);
          load();
        }}
      />
    </div>
  );
}
